// src/services/allowanceService.js
// COMPLETE REPLACEMENT - WITH PER_DAY CALCULATION

import { supabase } from './supabase';
import moment from 'moment';

export const allowanceService = {
  /**
   * Get all allowance types for a company
   */
  async getAllowanceTypes(company_id) {
    try {
      const { data, error } = await supabase
        .from('allowance_types')
        .select('*')
        .eq('company_id', company_id)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching allowance types:', error);
      throw error;
    }
  },

  /**
   * Create a new allowance type
   */
  async createAllowanceType(allowanceType) {
    try {
      // Set calculation_method based on is_percentage if not provided
      const calculationMethod = allowanceType.calculation_method || 
        (allowanceType.is_percentage ? 'percentage' : 'fixed');

      const { data, error } = await supabase
        .from('allowance_types')
        .insert([{
          ...allowanceType,
          calculation_method: calculationMethod,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating allowance type:', error);
      throw error;
    }
  },

  /**
   * Update an allowance type
   */
  async updateAllowanceType(id, updates) {
    try {
      const { data, error } = await supabase
        .from('allowance_types')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating allowance type:', error);
      throw error;
    }
  },

  /**
   * Delete an allowance type (soft delete)
   */
  async deleteAllowanceType(id) {
    try {
      const { error } = await supabase
        .from('allowance_types')
        .update({
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting allowance type:', error);
      throw error;
    }
  },

  /**
   * Get employee allowances
   */
  async getEmployeeAllowances(employee_id, company_id) {
    try {
      const { data, error } = await supabase
        .from('employee_allowances')
        .select(`
          *,
          allowance_types (
            id,
            name,
            description,
            is_percentage,
            is_taxable,
            calculation_method
          )
        `)
        .eq('employee_id', employee_id)
        .eq('company_id', company_id)
        .order('start_date', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching employee allowances:', error);
      throw error;
    }
  },

  /**
   * Get active employee allowances for a date range
   */
  async getActiveEmployeeAllowances(employee_id, company_id, startDate, endDate) {
    try {
      const { data, error } = await supabase
        .from('employee_allowances')
        .select(`
          *,
          allowance_types (
            id,
            name,
            description,
            is_percentage,
            is_taxable,
            calculation_method
          )
        `)
        .eq('employee_id', employee_id)
        .eq('company_id', company_id)
        .or(`start_date.lte.${endDate},end_date.gte.${startDate}`)
        .order('start_date');

      if (error) throw error;

      const activeAllowances = (data || []).filter(allowance => {
        const allowanceStart = moment(allowance.start_date);
        const allowanceEnd = moment(allowance.end_date);
        const periodStart = moment(startDate);
        const periodEnd = moment(endDate);

        return allowanceStart.isSameOrBefore(periodEnd) && allowanceEnd.isSameOrAfter(periodStart);
      });

      return activeAllowances;
    } catch (error) {
      console.error('Error fetching active employee allowances:', error);
      throw error;
    }
  },

  /**
   * Create employee allowance
   */
  async createEmployeeAllowance(allowance) {
    try {
      const { data, error } = await supabase
        .from('employee_allowances')
        .insert([{
          ...allowance,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating employee allowance:', error);
      throw error;
    }
  },

  /**
   * Update employee allowance
   */
  async updateEmployeeAllowance(id, updates) {
    try {
      const { data, error } = await supabase
        .from('employee_allowances')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating employee allowance:', error);
      throw error;
    }
  },

  /**
   * Delete employee allowance
   */
  async deleteEmployeeAllowance(id) {
    try {
      const { error } = await supabase
        .from('employee_allowances')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting employee allowance:', error);
      throw error;
    }
  },

  /**
   * Calculate allowance amount with PER_DAY support
   * @param {Object} allowance - Allowance record
   * @param {number} baseSalary - Base salary
   * @param {number} workingDays - Number of working days in period
   */
  calculateAllowanceAmount(allowance, baseSalary, workingDays = 0) {
    const calculationMethod = allowance.allowance_types?.calculation_method || 'fixed';

    switch (calculationMethod) {
      case 'percentage':
        // Percentage of base salary
        return baseSalary * (allowance.amount / 100);

      case 'per_day':
        // Amount × Working Days
        return allowance.amount * workingDays;

      case 'fixed':
      default:
        // Fixed amount
        return allowance.amount;
    }
  },

  /**
   * Get allowance summary for payroll period with PER_DAY support
   */
  async getAllowanceSummary(employee_id, company_id, startDate, endDate, baseSalary, workingDays) {
    try {
      const allowances = await this.getActiveEmployeeAllowances(
        employee_id, 
        company_id, 
        startDate, 
        endDate
      );

      let totalAllowances = 0;
      let taxableAllowances = 0;
      let nonTaxableAllowances = 0;
      const allowanceBreakdown = [];

      allowances.forEach(allowance => {
        const amount = this.calculateAllowanceAmount(allowance, baseSalary, workingDays);
        
        totalAllowances += amount;
        
        if (allowance.allowance_types?.is_taxable) {
          taxableAllowances += amount;
        } else {
          nonTaxableAllowances += amount;
        }

        allowanceBreakdown.push({
          id: allowance.id,
          name: allowance.allowance_types?.name || 'Unknown',
          amount: amount,
          calculation_method: allowance.allowance_types?.calculation_method || 'fixed',
          is_percentage: allowance.allowance_types?.is_percentage || false,
          is_taxable: allowance.allowance_types?.is_taxable || true,
          original_amount: allowance.amount,
          working_days: allowance.allowance_types?.calculation_method === 'per_day' ? workingDays : null,
          start_date: allowance.start_date,
          end_date: allowance.end_date
        });
      });

      return {
        totalAllowances,
        taxableAllowances,
        nonTaxableAllowances,
        allowanceBreakdown,
        allowanceCount: allowances.length
      };
    } catch (error) {
      console.error('Error calculating allowance summary:', error);
      throw error;
    }
  },

  /**
   * Create default allowance types including Transport (per_day)
   */
  async createDefaultAllowanceTypes(company_id) {
    const defaultTypes = [
      {
        company_id,
        name: 'Transport Allowance',
        description: 'Daily transportation allowance (amount × working days)',
        calculation_method: 'per_day',
        is_percentage: false,
        is_taxable: true
      },
      {
        company_id,
        name: 'Mobile Package',
        description: 'Monthly mobile phone and data allowance',
        calculation_method: 'fixed',
        is_percentage: false,
        is_taxable: true
      },
      {
        company_id,
        name: 'Meal Allowance',
        description: 'Daily meal allowance (amount × working days)',
        calculation_method: 'per_day',
        is_percentage: false,
        is_taxable: true
      },
      {
        company_id,
        name: 'Housing Allowance',
        description: 'Monthly housing/accommodation allowance',
        calculation_method: 'fixed',
        is_percentage: false,
        is_taxable: true
      },
      {
        company_id,
        name: 'Performance Bonus',
        description: 'Performance-based bonus (percentage of salary)',
        calculation_method: 'percentage',
        is_percentage: true,
        is_taxable: true
      },
      {
        company_id,
        name: 'Medical Allowance',
        description: 'Monthly medical insurance allowance',
        calculation_method: 'fixed',
        is_percentage: false,
        is_taxable: false
      }
    ];

    try {
      const { data, error } = await supabase
        .from('allowance_types')
        .insert(defaultTypes.map(type => ({
          ...type,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })))
        .select();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating default allowance types:', error);
      throw error;
    }
  }
};

export default allowanceService;